import { noop } from 'lodash'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import {
    WebViewerInstance,
    Annotations as AnnotationsType,
    Annotations,
} from '@pdftron/webviewer'
import {
    createSanitizedAttachmentMutation,
    markUploadedSanitizedAttachmentMutation,
} from '@Admin/BubbleDrawingPage/quoteModelRevisionAttachmentQuery'
import { qmrAttachemntQuery } from '@Admin/AdminQuotingPage/query'
import { getQmrAttachmentAnnotations } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/Query'

import { isSignedUriExpired } from '~/lib/assetUrlCache'
import { uploadFile } from '~/lib/uploadFile'
import logger from '~/lib/logger'
import { AttachmentCreateType } from '~/__generated__/globalTypes'

type ApolloClientType = ApolloClient<NormalizedCacheObject>

export const PDFViewerFLags = {
    HIGHLIGHT_STD_TOLERANCES: 'HIGHLIGHT_STD_TOLERANCES',
    DISAPLAY_CERTIFICATE_REQUIREMENT_HIGHLIGHTS:
        'DISAPLAY_CERTIFICATE_REQUIREMENT_HIGHLIGHTS',
}

export const reloadPdfViewer = async (props: {
    quoteModelRevisionId: string
    attachmentId: string
    pdfUrl: string
    instance: WebViewerInstance
    client: ApolloClient<Object>
}) => {
    const {
        quoteModelRevisionId,
        attachmentId,
        instance,
        instance: { docViewer },
        client,
    } = props
    let { pdfUrl } = props

    if (isSignedUriExpired(pdfUrl)) {
        const qmrAttachment = await client.query({
            query: qmrAttachemntQuery,
            variables: { id: quoteModelRevisionId, showDeleted: true },
            fetchPolicy: 'network-only',
        })

        pdfUrl =
            qmrAttachment?.data?.quoteModelRevision?.attachmentLink
                ?.quoteAttachmentRevision?.attachment?.downloadUrl || pdfUrl
    }

    const qmrAttachmentAnnotations = await client.query({
        query: getQmrAttachmentAnnotations,
        variables: {
            attachmentId: attachmentId,
            quoteModelRevisionId: quoteModelRevisionId,
        },
        fetchPolicy: 'network-only',
    })
    const annotationsDataDownloadUrl =
        qmrAttachmentAnnotations?.data?.getQmrAttachmentAnnotations
            ?.annotationsDataDownloadUrl

    if (annotationsDataDownloadUrl) {
        docViewer.setDocumentXFDFRetriever(async () => {
            const qmrAttachmentAnnotationsData = await fetch(
                annotationsDataDownloadUrl,
            )
            const xfdfString = await qmrAttachmentAnnotationsData.text()

            return xfdfString
        })
    }
    instance.loadDocument(pdfUrl)
}

export const uploadSanitizedPdf = async (props: {
    attachmentId: string
    quoteModelRevisionId: string
    title: string
    pdfBlob: Blob
    pdfUrl: string
    client: ApolloClientType
    instance: WebViewerInstance
}) => {
    const {
        attachmentId,
        quoteModelRevisionId,
        title,
        pdfBlob,
        pdfUrl,
        client,
        instance,
    } = props

    try {
        const response = await client
            .mutate({
                mutation: createSanitizedAttachmentMutation,
                variables: {
                    attachmentId,
                    uploadMetadata: {
                        fileName: `${title.replace('.pdf', '_sanitized.pdf')}`,
                        extension: '.pdf',
                        mimeType: 'application/pdf',
                        type: AttachmentCreateType.user_uploaded,
                    },
                    quoteModelRevisionId,
                },
            })
            .catch(error => {
                logger.exception(error, {
                    extra: {
                        info: 'createSanitizedAttachmentMutation mutation failed',
                        attachmentId,
                    },
                })
            })

        const {
            data: {
                createSanitizedAttachment: {
                    id: sanitizedAttachmentId,
                    uploadUrl: sanitizedAttachmentUploadUrl,
                },
            },
        } = response || {}

        await uploadFile(
            pdfBlob,
            sanitizedAttachmentUploadUrl || '',
            noop,
            true,
        )

        await client
            .mutate({
                mutation: markUploadedSanitizedAttachmentMutation,
                variables: {
                    id: sanitizedAttachmentId,
                },
            })
            .catch(error => {
                logger.exception(error, {
                    extra: {
                        info: 'markUploadedSanitizedAttachmentMutation mutation failed',
                        attachmentId,
                        sanitizedAttachmentId,
                    },
                })
            })
        await reloadPdfViewer({
            attachmentId,
            quoteModelRevisionId,
            pdfUrl,
            instance,
            client,
        })
    } catch (error) {
        logger.exception(error, {
            extra: {
                info: 'Failed to update Sanitized drawing',
                attachmentId,
            },
        })
    }
}

const sortAnnotations = (a: any, b: any) => {
    const annotationCreatedAtA = Date.parse(a.DateCreated)
    const annotationCreatedAtB = Date.parse(b.DateCreated)

    return annotationCreatedAtA - annotationCreatedAtB
}

export const renumberInspectionPoint = ({
    totalPages,
    annotationsList,
    reDrawInspectionPoint,
}: {
    totalPages: number
    annotationsList: AnnotationsType.Annotation[]
    reDrawInspectionPoint: (arg: AnnotationsType.Annotation) => void
}) => {
    let index = 1

    const setInspectionPointMetaData = (
        annotation: AnnotationsType.Annotation,
    ) => {
        if (annotation.Subject === 'InspectionPointBubble') {
            annotation.setCustomData('index', index)
            reDrawInspectionPoint(annotation)
            index = index + 1
        }
    }

    if (totalPages > 1) {
        annotationsList
            .filter(annotation => 'DateCreated' in annotation)
            .sort(sortAnnotations)
            .forEach(setInspectionPointMetaData)
    } else {
        annotationsList.forEach(setInspectionPointMetaData)
    }
}

export const getBoundingBoxCoordinates = (
    pageWidth: number,
    pageHeight: number,
    boundingBox: any,
    rotation: number,
) => {
    if (rotation == 0) {
        // NO Rotation
        return {
            xmax: boundingBox.xmax * pageWidth,
            xmin: boundingBox.xmin * pageWidth,
            ymax: boundingBox.ymax * pageHeight,
            ymin: boundingBox.ymin * pageHeight,
        }
    } else if (rotation == 1) {
        //90 Degree Rotation
        return {
            xmax: boundingBox.ymin * pageWidth,
            xmin: boundingBox.ymax * pageWidth,
            ymax: (1 - boundingBox.xmax) * pageHeight,
            ymin: (1 - boundingBox.xmin) * pageHeight,
        }
    } else if (rotation == 2) {
        //180 Degree Rotation
        return {
            xmax: (1 - boundingBox.xmax) * pageWidth,
            xmin: (1 - boundingBox.xmin) * pageWidth,
            ymax: (1 - boundingBox.ymax) * pageHeight,
            ymin: (1 - boundingBox.ymin) * pageHeight,
        }
    } else {
        return {
            xmax: boundingBox.xmax * pageWidth,
            xmin: boundingBox.xmin * pageWidth,
            ymax: boundingBox.ymax * pageHeight,
            ymin: boundingBox.ymin * pageHeight,
        }
    }
}

export const getAnnotationsToSave = (
    instance: WebViewerInstance,
    attachmentId: string | null = null,
) => {
    const allAnnotations = instance.annotManager.getAnnotationsList()
    const annotationsToSave = allAnnotations.filter(
        (annotation: Annotations.Annotation) => {
            if (
                annotation.Subject === 'ToleranceAnnotation' ||
                annotation.Subject === 'ThreadAnnotation' ||
                annotation.Subject === 'GDTAnnotation' ||
                (attachmentId &&
                    annotation.getCustomData('attachmentId') !== attachmentId)
            ) {
                return false
            } else {
                return true
            }
        },
    )

    return annotationsToSave
}

export const getXFDFToSave = async (
    instance: WebViewerInstance,
    attachmentId: string | null = null,
) => {
    const annotationsToSave = getAnnotationsToSave(instance, attachmentId)
    const xfdfString = await instance.annotManager.exportAnnotations({
        annotList: annotationsToSave,
    })

    return xfdfString
}
